import React, { useState, useEffect } from 'react';
import './AccessibilityToolbar.css';

const AccessibilityToolbar = () => {
    const [isOpen, setIsOpen] = useState(false);
    const [fontSize, setFontSize] = useState(16);
    const [highContrast, setHighContrast] = useState(false);
    const [grayscale, setGrayscale] = useState(false);
    const [negativeContrast, setNegativeContrast] = useState(false);
    const [lightBackground, setLightBackground] = useState(false);
    const [linksUnderline, setLinksUnderline] = useState(false);
    const [readableFont, setReadableFont] = useState(false);

    useEffect(() => {
        document.documentElement.style.fontSize = `${fontSize}px`;
    }, [fontSize]);

    const handleFontSizeIncrease = () => {
        setFontSize(prev => Math.min(prev + 2, 24));
    };

    const handleFontSizeDecrease = () => {
        setFontSize(prev => Math.max(prev - 2, 12));
    };

    const toggleHighContrast = () => {
        setHighContrast(!highContrast);
        document.body.classList.toggle('high-contrast');
    };

    const toggleGrayscale = () => {
        setGrayscale(!grayscale);
        document.body.classList.toggle('grayscale');
    };

    const toggleNegativeContrast = () => {
        setNegativeContrast(!negativeContrast);
        document.body.classList.toggle('negative-contrast');
    };

    const toggleLightBackground = () => {
        setLightBackground(!lightBackground);
        document.body.classList.toggle('light-background');
    };

    const toggleLinksUnderline = () => {
        setLinksUnderline(!linksUnderline);
        document.body.classList.toggle('links-underline');
    };

    const toggleReadableFont = () => {
        setReadableFont(!readableFont);
        document.body.classList.toggle('readable-font');
    };

    const resetAll = () => {
        setFontSize(16);
        setHighContrast(false);
        setGrayscale(false);
        setNegativeContrast(false);
        setLightBackground(false);
        setLinksUnderline(false);
        setReadableFont(false);
        document.body.classList.remove(
            'high-contrast',
            'grayscale',
            'negative-contrast',
            'light-background',
            'links-underline',
            'readable-font'
        );
    };

    return (
        <div className={`pojo-a11y-toolbar ${isOpen ? 'active' : ''}`}>
            <div className="pojo-a11y-toolbar-toggle" onClick={() => setIsOpen(!isOpen)} aria-label="Accessibility Tools" role="button" tabIndex="0">
                <span className="pojo-a11y-toolbar-icon">
                    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 448 512" width="1em" height="1em">
                        <path fill="currentColor" d="M423.9 255.8L411 413.1c-3.3 40.7-63.9 35.1-60.6-4.9l10-122.5-41.1 2.3c10.1 20.7 15.8 43.9 15.8 68.5 0 41.2-16.1 78.7-42.3 106.5l-39.3-39.3c57.9-63.7 13.1-167.2-74-167.2-25.9 0-49.5 9.9-67.2 26L73 243.2c22-20.7 50.1-35.1 81.4-40.2l75.3-85.7-42.6-24.8-51.6 46c-30 26.8-70.6-18.5-40.5-45.4l68-60.7c9.8-8.8 24.1-10.2 35.5-3.6 0 0 139.3 80.9 139.5 81.1 16.2 10.1 20.7 36 6.1 52.6L285.7 229l106.1-5.9c18.5-1.1 33.6 14.4 32.1 32.7zm-64.9-154c28.1 0 50.9-22.8 50.9-50.9C409.9 22.8 387.1 0 359 0c-28.1 0-50.9 22.8-50.9 50.9 0 28.1 22.8 50.9 50.9 50.9zM179.6 456.5c-80.6 0-127.4-90.6-82.7-156.1l-39.7-39.7C36.4 287 24 320.3 24 356.4c0 130.7 150.7 201.4 251.4 122.5l-39.7-39.7c-16 10.9-35.3 17.3-56.1 17.3z"/>
                    </svg>
                </span>
            </div>
            
            <ul className="pojo-a11y-toolbar-items">
                <li className="pojo-a11y-toolbar-item">
                    <button onClick={handleFontSizeIncrease} className="pojo-a11y-toolbar-link" aria-label="Increase text size">
                        <span className="pojo-a11y-toolbar-icon">
                            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" width="1em" height="1em">
                                <path fill="currentColor" d="M18 12.998h-4v4h-2v-4H8v-2h4v-4h2v4h4v2zm-6-10c-5.52 0-10 4.48-10 10s4.48 10 10 10 10-4.48 10-10-4.48-10-10-10zm0 18c-4.41 0-8-3.59-8-8s3.59-8 8-8 8 3.59 8 8-3.59 8-8 8z"/>
                            </svg>
                        </span>
                        <span className="pojo-a11y-toolbar-text">Larger Text</span>
                    </button>
                </li>

                <li className="pojo-a11y-toolbar-item">
                    <button onClick={handleFontSizeDecrease} className="pojo-a11y-toolbar-link" aria-label="Decrease text size">
                        <span className="pojo-a11y-toolbar-icon">
                            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" width="1em" height="1em">
                                <path fill="currentColor" d="M18 13H6v-2h12v2zm-6-10C6.48 3 2 7.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 3 12 3zm0 18c-4.41 0-8-3.59-8-8s3.59-8 8-8 8 3.59 8 8-3.59 8-8 8z"/>
                            </svg>
                        </span>
                        <span className="pojo-a11y-toolbar-text">Smaller Text</span>
                    </button>
                </li>

                <li className="pojo-a11y-toolbar-item">
                    <button onClick={toggleHighContrast} className={`pojo-a11y-toolbar-link ${highContrast ? 'active' : ''}`} aria-label="Toggle high contrast">
                        <span className="pojo-a11y-toolbar-icon">
                            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" width="1em" height="1em">
                                <path fill="currentColor" d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm0 18V4c4.41 0 8 3.59 8 8s-3.59 8-8 8z"/>
                            </svg>
                        </span>
                        <span className="pojo-a11y-toolbar-text">High Contrast</span>
                    </button>
                </li>

                <li className="pojo-a11y-toolbar-item">
                    <button onClick={toggleGrayscale} className={`pojo-a11y-toolbar-link ${grayscale ? 'active' : ''}`} aria-label="Toggle grayscale">
                        <span className="pojo-a11y-toolbar-icon">
                            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" width="1em" height="1em">
                                <path fill="currentColor" d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-1 17.93c-3.94-.49-7-3.85-7-7.93s3.06-7.44 7-7.93v15.86zm2-15.86c1.03.13 2 .45 2.87.93H13v-.93zM13 7h5.24c.25.31.48.65.68 1H13V7zm0 3h6.74c.08.33.15.66.19 1H13v-1zm0 3h6.93c-.04.34-.11.67-.19 1H13v-1zm0 3h5.92c-.2.35-.43.69-.68 1H13v-1zm0 3h2.87c-.87.48-1.84.8-2.87.93v-.93z"/>
                            </svg>
                        </span>
                        <span className="pojo-a11y-toolbar-text">Grayscale</span>
                    </button>
                </li>

                <li className="pojo-a11y-toolbar-item">
                    <button onClick={toggleLinksUnderline} className={`pojo-a11y-toolbar-link ${linksUnderline ? 'active' : ''}`} aria-label="Toggle links underline">
                        <span className="pojo-a11y-toolbar-icon">
                            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" width="1em" height="1em">
                                <path fill="currentColor" d="M15 3h4c.55 0 1 .45 1 1v16c0 .55-.45 1-1 1h-4c-.55 0-1-.45-1-1s.45-1 1-1h3V5h-3c-.55 0-1-.45-1-1s.45-1 1-1zM5 5v14h3c.55 0 1 .45 1 1s-.45 1-1 1H4c-.55 0-1-.45-1-1V4c0-.55.45-1 1-1h4c.55 0 1 .45 1 1s-.45 1-1 1H5z"/>
                            </svg>
                        </span>
                        <span className="pojo-a11y-toolbar-text">Highlight Links</span>
                    </button>
                </li>

                <li className="pojo-a11y-toolbar-item">
                    <button onClick={toggleReadableFont} className={`pojo-a11y-toolbar-link ${readableFont ? 'active' : ''}`} aria-label="Toggle readable font">
                        <span className="pojo-a11y-toolbar-icon">
                            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" width="1em" height="1em">
                                <path fill="currentColor" d="M9.93 13.5h4.14L12 7.98zM20 2H4c-1.1 0-2 .9-2 2v16c0 1.1.9 2 2 2h16c1.1 0 2-.9 2-2V4c0-1.1-.9-2-2-2zm-4.05 16.5l-1.14-3H9.17l-1.12 3H5.96l5.11-13h1.86l5.11 13h-2.09z"/>
                            </svg>
                        </span>
                        <span className="pojo-a11y-toolbar-text">Readable Font</span>
                    </button>
                </li>

                <li className="pojo-a11y-toolbar-item">
                    <button onClick={resetAll} className="pojo-a11y-toolbar-link" aria-label="Reset all settings">
                        <span className="pojo-a11y-toolbar-icon">
                            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" width="1em" height="1em">
                                <path fill="currentColor" d="M12 5V2L8 6l4 4V7c3.31 0 6 2.69 6 6 0 2.97-2.17 5.43-5 5.91v2.02c3.95-.49 7-3.85 7-7.93 0-4.42-3.58-8-8-8zm-6 8c0-1.65.67-3.15 1.76-4.24L6.34 7.34C4.9 8.79 4 10.79 4 13c0 4.08 3.05 7.44 7 7.93v-2.02c-2.83-.48-5-2.94-5-5.91z"/>
                            </svg>
                        </span>
                        <span className="pojo-a11y-toolbar-text">Reset Settings</span>
                    </button>
                </li>
            </ul>
        </div>
    );
};

export default AccessibilityToolbar; 